import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  ChipDelete,
  Dropdown,
  IconButton,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowsDownUp,
  CaretDown,
  CaretUp,
  DotsSixVertical,
  DotsThree,
  PencilSimpleLine,
  Plus,
  SignOut,
  SquaresFour,
  Stack as StackIcon,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  AttributeColorSwatch,
  getAttributeValueKey,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import { MIN_ATTRIBUTES_TO_ORDER } from 'companySettings/components/PartFinderProductTypes/UpsertPartFinderProductType/constants';
import {
  IOrderItem,
  OrderPanel,
} from 'companySettings/components/PartFinderProductTypes/UpsertPartFinderProductType/OrderPanel';
import { UpsertProductTypeAttribute } from 'companySettings/components/PartFinderProductTypes/UpsertPartFinderProductType/UpsertProductTypeAttribute';
import { AttributeType } from 'companySettings/enums';
import { useGetPublishedPartFinderProductTypes } from 'companySettings/hooks';
import {
  IPartFinderAttributeValue,
  IPartFinderFamilyAttribute,
  IPartFinderProductTypeAttribute,
} from 'companySettings/types';
import { Button } from 'core/components';
import { useFormContext } from 'core/states';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { GroupNamePanel, MoveToGroupPanel } from './GroupPanels';
import {
  detachFromGroup,
  getAttributeUnits,
  getGroupKey,
  getUnitIndexes,
  groupAttributes,
  IAttributeUnit,
  MIN_GROUP_SIZE,
  moveToGroup,
  moveUnit,
  moveWithinGroup,
  normalizeGroups,
  renameGroup,
  ungroup,
} from './groups';
import {
  isInheritedAttribute,
  mapFamilyAttributeFromDrawer,
  mapFamilyAttributeToDrawer,
} from './mappers';
import { AttributeChangeState } from './types';
import { getAttributeChangeState, getAttributeKey, prunePartValues } from './utils';

// A card is keyed as its attribute's part values are (getAttributeKey()): unique in the family and
// stable through a rename.
const getCardId = getAttributeKey;

interface ICardProps {
  attribute: IPartFinderFamilyAttribute;
  isFirst: boolean;
  isLast: boolean;
  onToggleValue: (value: IPartFinderAttributeValue) => void;
  onRemoveValue: (value: IPartFinderAttributeValue) => void;
  onToggleUse: () => void;
  onDefineValuesOrder: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onRemove: () => void;
  // An attribute on its own can be selected and moved into a group; one in a group detached.
  isSelected?: boolean;
  onSelect?: () => void;
  onMoveToGroup?: () => void;
  onDetach?: () => void;
  // In the family editor: whether the attribute is new or changed since the family was saved.
  changeState?: AttributeChangeState | null;
}

// One attribute of the family. An inherited one shows every value of the version it was taken
// from, the ones the family uses switched on, then the ones the family added; a Number & Range one
// takes the product type's range as it is. A family-specific one shows its own values, each
// removable, as on a product type card. Edit changes the values of either. One the family does not
// use is greyed out without its values.
const FamilyAttributeCard = ({
  attribute,
  isFirst,
  isLast,
  onToggleValue,
  onRemoveValue,
  onToggleUse,
  onDefineValuesOrder,
  onMoveUp,
  onMoveDown,
  onEdit,
  onRemove,
  isSelected,
  onSelect,
  onMoveToGroup,
  onDetach,
  changeState,
}: ICardProps) => {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: getCardId(attribute),
  });

  const isInherited = isInheritedAttribute(attribute);
  const isRange = AttributeType.NUMBER_RANGE === attribute.type;
  const selectedKeys = new Set(
    attribute.values.map((value) => getAttributeValueKey(attribute.type, value))
  );
  // An inherited attribute shows the values the family uses in the family's order - the one
  // Define Values Order sets and the save keeps - then the ones of the version it was taken from
  // that the family does not use.
  const shownValues = isInherited
    ? [
        ...attribute.values,
        ...(attribute.sourceValues ?? []).filter(
          (value) => !selectedKeys.has(getAttributeValueKey(attribute.type, value))
        ),
      ]
    : attribute.values;

  return (
    <Card
      ref={setNodeRef}
      variant={Variant.OUTLINED}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging || !attribute.inUse ? 0.5 : 1,
      }}
    >
      <CardContent>
        <Stack sx={{ gap: 2 }}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Box
              {...attributes}
              {...listeners}
              sx={{ display: 'flex', color: 'neutral.500', cursor: 'grab', touchAction: 'none' }}
            >
              <DotsSixVertical weight="bold" />
            </Box>

            {onSelect && <Checkbox checked={Boolean(isSelected)} onChange={onSelect} />}

            <Typography level={Level.TITLE_MD}>{attribute.name}</Typography>
            <Chip
              size={Size.SM}
              variant={Variant.SOFT}
              color={isInherited ? Color.PRIMARY : Color.SUCCESS}
            >
              {t(isInherited ? 'labelInheritedAttributes' : 'labelFamilyAttributes')}
            </Chip>
            {changeState && (
              <Chip
                size={Size.SM}
                variant={Variant.SOLID}
                color={AttributeChangeState.NEW === changeState ? Color.SUCCESS : Color.WARNING}
              >
                {t(
                  AttributeChangeState.NEW === changeState
                    ? 'labelUnsavedNew'
                    : 'labelUnsavedChanged'
                )}
              </Chip>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Dropdown>
              <Tooltip title={t('labelMoreActions')}>
                {/* The theme gives every menu button a caret; this one is just the ⋯ icon. */}
                <MenuButton
                  endDecorator={null}
                  slots={{ root: IconButton }}
                  slotProps={{
                    root: { variant: Variant.SOFT, color: Color.NEUTRAL, size: Size.SM },
                  }}
                >
                  <DotsThree weight="bold" />
                </MenuButton>
              </Tooltip>
              {/* Above the panel the card sits in: menus default to a layer below modals. */}
              <Menu placement="bottom-end" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <MenuItem onClick={onToggleUse}>
                  <ListItemDecorator>
                    <Switch size={Size.SM} checked={attribute.inUse} />
                  </ListItemDecorator>
                  {t('buttonLabelUseInThisFamily')}
                </MenuItem>
                <MenuItem onClick={onDefineValuesOrder} disabled={attribute.values.length < 2}>
                  <ListItemDecorator>
                    <ArrowsDownUp />
                  </ListItemDecorator>
                  {t('buttonLabelDefineValuesOrder')}
                </MenuItem>
                <MenuItem onClick={onMoveUp} disabled={isFirst}>
                  <ListItemDecorator>
                    <CaretUp />
                  </ListItemDecorator>
                  {t('buttonLabelMoveUp')}
                </MenuItem>
                <MenuItem onClick={onMoveDown} disabled={isLast}>
                  <ListItemDecorator>
                    <CaretDown />
                  </ListItemDecorator>
                  {t('buttonLabelMoveDown')}
                </MenuItem>
                {onMoveToGroup && (
                  <MenuItem onClick={onMoveToGroup}>
                    <ListItemDecorator>
                      <StackIcon />
                    </ListItemDecorator>
                    {t('buttonLabelMoveToGroup')}
                  </MenuItem>
                )}
                <MenuItem onClick={onEdit}>
                  <ListItemDecorator>
                    <PencilSimpleLine />
                  </ListItemDecorator>
                  {t('buttonLabelEdit')}
                </MenuItem>
                {onDetach && (
                  <MenuItem onClick={onDetach}>
                    <ListItemDecorator>
                      <SignOut />
                    </ListItemDecorator>
                    {t('buttonLabelDetachFromGroup')}
                  </MenuItem>
                )}
                {!isInherited && (
                  <MenuItem onClick={onRemove} color={Color.DANGER}>
                    <ListItemDecorator>
                      <Trash />
                    </ListItemDecorator>
                    {t('buttonLabelRemove')}
                  </MenuItem>
                )}
              </Menu>
            </Dropdown>
          </Stack>

          {attribute.inUse && (
            <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {shownValues.map((value) => {
                const key = getAttributeValueKey(attribute.type, value);
                const label = getAttributeValueLabel(attribute.type, value, t);
                const swatch = AttributeType.COLOUR === attribute.type && (
                  <AttributeColorSwatch colorHex={value.colorHex} />
                );

                if (isRange && isInherited) {
                  return (
                    <Chip key={key} variant={Variant.SOLID} color={Color.PRIMARY}>
                      {t('labelRangeFromProductType', { range: label })}
                    </Chip>
                  );
                }

                if (!isInherited) {
                  return (
                    <Chip
                      key={key}
                      variant={Variant.SOLID}
                      color={Color.PRIMARY}
                      startDecorator={swatch}
                      endDecorator={<ChipDelete onDelete={() => onRemoveValue(value)} />}
                    >
                      {label}
                    </Chip>
                  );
                }

                const isSelected = selectedKeys.has(key);

                return (
                  <Chip
                    key={key}
                    variant={isSelected ? Variant.SOLID : Variant.SOFT}
                    color={isSelected ? Color.PRIMARY : Color.NEUTRAL}
                    startDecorator={swatch}
                    onClick={() => onToggleValue(value)}
                  >
                    {label}
                  </Chip>
                );
              })}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const getUnitId = (attributes: IPartFinderFamilyAttribute[], unit: IAttributeUnit) =>
  'group' === unit.type ? `group-${getGroupKey(unit.name)}` : getCardId(attributes[unit.index]);

interface IGroupCardProps {
  id: string;
  name: string;
  isInUse: boolean;
  isFirst: boolean;
  isLast: boolean;
  memberIds: string[];
  children: JSX.Element[];
  onToggleUse: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDefineOrder: () => void;
  onEdit: () => void;
  onUngroup: () => void;
  onReorder: (from: number, to: number) => void;
}

// A group of attributes: its name and menu, then its attributes, which can be dragged among
// themselves. "Use in this Family" switches all of them.
const GroupCard = ({
  id,
  name,
  isInUse,
  isFirst,
  isLast,
  memberIds,
  children,
  onToggleUse,
  onMoveUp,
  onMoveDown,
  onDefineOrder,
  onEdit,
  onUngroup,
  onReorder,
}: IGroupCardProps) => {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    onReorder(memberIds.indexOf(String(active.id)), memberIds.indexOf(String(over.id)));
  };

  return (
    <Card
      ref={setNodeRef}
      variant={Variant.OUTLINED}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <CardContent>
        <Stack sx={{ gap: 2 }}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Box
              {...attributes}
              {...listeners}
              sx={{ display: 'flex', color: 'neutral.500', cursor: 'grab', touchAction: 'none' }}
            >
              <DotsSixVertical weight="bold" />
            </Box>

            <Typography level={Level.TITLE_MD}>{name}</Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Dropdown>
              <Tooltip title={t('labelMoreActions')}>
                <MenuButton
                  endDecorator={null}
                  slots={{ root: IconButton }}
                  slotProps={{
                    root: { variant: Variant.SOFT, color: Color.NEUTRAL, size: Size.SM },
                  }}
                >
                  <DotsThree weight="bold" />
                </MenuButton>
              </Tooltip>
              <Menu placement="bottom-end" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <MenuItem onClick={onToggleUse}>
                  <ListItemDecorator>
                    <Switch size={Size.SM} checked={isInUse} />
                  </ListItemDecorator>
                  {t('buttonLabelUseInThisFamily')}
                </MenuItem>
                <MenuItem onClick={onMoveUp} disabled={isFirst}>
                  <ListItemDecorator>
                    <CaretUp />
                  </ListItemDecorator>
                  {t('buttonLabelMoveUp')}
                </MenuItem>
                <MenuItem onClick={onMoveDown} disabled={isLast}>
                  <ListItemDecorator>
                    <CaretDown />
                  </ListItemDecorator>
                  {t('buttonLabelMoveDown')}
                </MenuItem>
                <MenuItem onClick={onDefineOrder}>
                  <ListItemDecorator>
                    <ArrowsDownUp />
                  </ListItemDecorator>
                  {t('buttonLabelDefineAttributesOrder')}
                </MenuItem>
                <MenuItem onClick={onEdit}>
                  <ListItemDecorator>
                    <PencilSimpleLine />
                  </ListItemDecorator>
                  {t('buttonLabelEdit')}
                </MenuItem>
                <MenuItem onClick={onUngroup} color={Color.DANGER}>
                  <ListItemDecorator>
                    <SquaresFour />
                  </ListItemDecorator>
                  {t('buttonLabelUngroup')}
                </MenuItem>
              </Menu>
            </Dropdown>
          </Stack>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={memberIds} strategy={verticalListSortingStrategy}>
              <Stack sx={{ gap: 1.5 }}>{children}</Stack>
            </SortableContext>
          </DndContext>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Step 2: the attributes the family inherits from its product type and its own ones, in the
// family's order, some of them in groups. Nothing here is saved on its own: it all goes with the
// family's save.
interface IProps {
  // The saved family's attributes, in the editor: the cards that differ from them are marked.
  savedAttributes?: IPartFinderFamilyAttribute[];
  // Where the buttons above the cards go instead, e.g. the drawer's sticky header, so they stay
  // in view while the cards scroll.
  toolbarContainer?: HTMLElement | null;
}

export const AttributesStep = ({ savedAttributes, toolbarContainer }: IProps = {}) => {
  const { t } = useTranslation();

  // The attribute drawer: closed, adding (no index) or editing the attribute at an index.
  const [attributePanel, setAttributePanel] = useState<{ index?: number } | null>(null);
  const [orderingValuesIndex, setOrderingValuesIndex] = useState<number | null>(null);
  const [isAttributesOrderOpen, setIsAttributesOrderOpen] = useState(false);
  // The group whose attributes Define Attributes Order is ordering.
  const [orderingGroup, setOrderingGroup] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // The group drawer: naming the selected attributes' new group, or renaming one.
  const [groupPanel, setGroupPanel] = useState<{ name?: string } | null>(null);
  const [movingIndex, setMovingIndex] = useState<number | null>(null);

  const { watch, setValue, getValues } = useFormContext();

  const attributes: IPartFinderFamilyAttribute[] = watch('attributes') ?? [];
  const productTypeId: number | null = watch('productTypeId');

  const { records: productTypes = [] } = useGetPublishedPartFinderProductTypes();
  const productTypeName = productTypes.find(({ id }) => id === productTypeId)?.name ?? '';
  const hasFamilyAttributes = attributes.some((attribute) => !isInheritedAttribute(attribute));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const units = getAttributeUnits(attributes);
  const groupNames = units.flatMap((unit) => ('group' === unit.type ? [unit.name] : []));
  const selectedIndexes = attributes.flatMap((attribute, index) =>
    !attribute.group && selectedIds.includes(getCardId(attribute)) ? [index] : []
  );
  const hasInheritedSelected = selectedIndexes.some((index) =>
    isInheritedAttribute(attributes[index])
  );

  // Every change goes through here: groups stay together, the parts lose the values an attribute
  // no longer has, and only attributes still on their own stay selected.
  const setAttributes = (next: IPartFinderFamilyAttribute[]) => {
    const normalized = normalizeGroups(next);
    const standaloneIds = new Set(
      normalized.filter((attribute) => !attribute.group).map(getCardId)
    );

    setValue('attributes', normalized, { shouldDirty: true });
    setValue('parts', prunePartValues(getValues('parts') ?? [], normalized), { shouldDirty: true });
    setSelectedIds((current) => current.filter((id) => standaloneIds.has(id)));
  };

  const updateAttribute = (index: number, attribute: IPartFinderFamilyAttribute) =>
    setAttributes(
      attributes.map((current, currentIndex) => (currentIndex === index ? attribute : current))
    );

  const onMoveUnit = (from: number, to: number) => setAttributes(moveUnit(attributes, from, to));

  // A value switched on goes last; Define Values Order sets the family's order.
  const toggleValue = (index: number, toggled: IPartFinderAttributeValue) => {
    const attribute = attributes[index];
    const toggledKey = getAttributeValueKey(attribute.type, toggled);
    const isSelected = attribute.values.some(
      (value) => getAttributeValueKey(attribute.type, value) === toggledKey
    );

    updateAttribute(index, {
      ...attribute,
      values: isSelected
        ? attribute.values.filter(
            (value) => getAttributeValueKey(attribute.type, value) !== toggledKey
          )
        : [...attribute.values, toggled],
    });
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id]
    );

  const removeSelected = () => {
    setAttributes(attributes.filter((_, index) => !selectedIndexes.includes(index)));
    setSelectedIds([]);
  };

  const onSaveGroupName = (name: string) => {
    if (groupPanel?.name) {
      setAttributes(renameGroup(attributes, groupPanel.name, name));

      return;
    }

    setAttributes(groupAttributes(attributes, selectedIndexes, name));
    setSelectedIds([]);
  };

  const editedAttribute =
    attributePanel?.index !== undefined ? attributes[attributePanel.index] : undefined;
  const existingNames = attributes
    .filter((_, index) => index !== attributePanel?.index)
    .map((attribute) => attribute.name);

  const onAttributeSaved = (attribute: IPartFinderProductTypeAttribute) => {
    if (attributePanel?.index !== undefined) {
      updateAttribute(
        attributePanel.index,
        mapFamilyAttributeFromDrawer(attribute, editedAttribute)
      );
    } else {
      setAttributes([...attributes, mapFamilyAttributeFromDrawer(attribute)]);
    }

    setAttributePanel(null);
  };

  const orderingAttribute =
    null === orderingValuesIndex ? undefined : attributes[orderingValuesIndex];
  const valueOrderItems: IOrderItem[] = (orderingAttribute?.values ?? []).map((value, index) => ({
    id: String(index),
    label: getAttributeValueLabel(orderingAttribute.type, value, t),
    colorHex: AttributeType.COLOUR === orderingAttribute.type ? value.colorHex : undefined,
  }));
  // The family's order is one of attributes and groups; a group orders its own attributes.
  const unitOrderItems: IOrderItem[] = units.map((unit, index) => ({
    id: String(index),
    label: 'group' === unit.type ? unit.name : attributes[unit.index].name,
  }));
  const orderedGroupUnit = units.find(
    (unit) =>
      'group' === unit.type &&
      orderingGroup &&
      getGroupKey(unit.name) === getGroupKey(orderingGroup)
  );
  const orderedGroupIndexes = orderedGroupUnit ? getUnitIndexes(orderedGroupUnit) : [];
  const groupOrderItems: IOrderItem[] = orderedGroupIndexes.map((index, position) => ({
    id: String(position),
    label: attributes[index].name,
  }));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const unitIds = units.map((unit) => getUnitId(attributes, unit));

    onMoveUnit(unitIds.indexOf(String(active.id)), unitIds.indexOf(String(over.id)));
  };

  const savedByKey = useMemo(
    () =>
      savedAttributes &&
      new Map(savedAttributes.map((attribute) => [getAttributeKey(attribute), attribute])),
    [savedAttributes]
  );

  const renderCard = (index: number, unitPosition: number, group?: IAttributeUnit) => {
    const attribute = attributes[index];
    const members = group ? getUnitIndexes(group) : [];
    const memberPosition = members.indexOf(index);
    const id = getCardId(attribute);

    return (
      <FamilyAttributeCard
        key={id}
        attribute={attribute}
        changeState={savedByKey && getAttributeChangeState(attribute, savedByKey)}
        isFirst={group ? 0 === memberPosition : 0 === unitPosition}
        isLast={group ? members.length - 1 === memberPosition : units.length - 1 === unitPosition}
        onToggleValue={(value) => toggleValue(index, value)}
        onRemoveValue={(value) => toggleValue(index, value)}
        onToggleUse={() => updateAttribute(index, { ...attribute, inUse: !attribute.inUse })}
        onDefineValuesOrder={() => setOrderingValuesIndex(index)}
        onMoveUp={() =>
          group
            ? setAttributes(
                moveWithinGroup(attributes, members, memberPosition, memberPosition - 1)
              )
            : onMoveUnit(unitPosition, unitPosition - 1)
        }
        onMoveDown={() =>
          group
            ? setAttributes(
                moveWithinGroup(attributes, members, memberPosition, memberPosition + 1)
              )
            : onMoveUnit(unitPosition, unitPosition + 1)
        }
        onEdit={() => setAttributePanel({ index })}
        onRemove={() => setAttributes(attributes.filter((_, current) => current !== index))}
        isSelected={selectedIds.includes(id)}
        onSelect={group ? undefined : () => toggleSelected(id)}
        onMoveToGroup={!group && groupNames.length > 0 ? () => setMovingIndex(index) : undefined}
        onDetach={group ? () => setAttributes(detachFromGroup(attributes, index)) : undefined}
      />
    );
  };

  const addButton = (
    <Button
      text={t('buttonLabelAddAttributes')}
      color={Color.SUCCESS}
      startDecorator={<Plus />}
      onClick={() => setAttributePanel({})}
    />
  );

  const toolbar = (
    <>
      {/* With attributes selected, the bar groups or removes them, as the design has it. */}
      {selectedIndexes.length > 0 && (
        <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
          <Button
            icon={<X />}
            variant={Variant.PLAIN}
            color={Color.NEUTRAL}
            onClick={() => setSelectedIds([])}
          />
          <Typography level={Level.TITLE_SM}>
            {t('labelAttributesSelected', { count: selectedIndexes.length })}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            text={t('buttonLabelGroup')}
            variant={Variant.OUTLINED}
            color={Color.NEUTRAL}
            startDecorator={<StackIcon />}
            disabled={selectedIndexes.length < MIN_GROUP_SIZE}
            onClick={() => setGroupPanel({})}
          />
          <Tooltip title={hasInheritedSelected ? t('labelInheritedAttributesCannotBeRemoved') : ''}>
            <span>
              <Button
                text={t('buttonLabelRemove')}
                variant={Variant.SOFT}
                color={Color.DANGER}
                startDecorator={<Trash />}
                disabled={hasInheritedSelected}
                onClick={removeSelected}
              />
            </span>
          </Tooltip>
        </Stack>
      )}

      {/* The design shows the family-specific block until the family has an attribute of its own,
          then its button above the cards. Define Attributes Order is there from two attributes on,
          as on a product type. */}
      {!selectedIndexes.length &&
        (hasFamilyAttributes || units.length >= MIN_ATTRIBUTES_TO_ORDER) && (
          <Stack direction={Direction.ROW} sx={{ justifyContent: 'space-between', gap: 2 }}>
            {hasFamilyAttributes ? addButton : <Box />}
            {units.length >= MIN_ATTRIBUTES_TO_ORDER && (
              <Button
                text={t('buttonLabelDefineAttributesOrder')}
                variant={Variant.OUTLINED}
                color={Color.NEUTRAL}
                startDecorator={<ArrowsDownUp />}
                onClick={() => setIsAttributesOrderOpen(true)}
              />
            )}
          </Stack>
        )}
    </>
  );

  return (
    <Stack sx={{ gap: 2 }}>
      {toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar}

      {!hasFamilyAttributes && (
        <Card variant={Variant.OUTLINED}>
          <CardContent>
            <Stack sx={{ alignItems: 'center', gap: 1, py: 2 }}>
              <Typography level={Level.TITLE_MD}>{t('labelFamilySpecificAttributes')}</Typography>
              <Typography
                level={Level.TITLE_SM}
                textAlign="center"
                sx={{ color: 'neutral.600', maxWidth: 360 }}
              >
                {t('labelFamilySpecificAttributesHint')}
              </Typography>
              {addButton}
            </Stack>
          </CardContent>
        </Card>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={units.map((unit) => getUnitId(attributes, unit))}
          strategy={verticalListSortingStrategy}
        >
          <Stack sx={{ gap: 2 }}>
            {units.map((unit, unitPosition) => {
              if ('attribute' === unit.type) {
                return renderCard(unit.index, unitPosition);
              }

              const members = getUnitIndexes(unit);
              const isInUse = members.some((index) => attributes[index].inUse);

              return (
                <GroupCard
                  key={getUnitId(attributes, unit)}
                  id={getUnitId(attributes, unit)}
                  name={unit.name}
                  isInUse={isInUse}
                  isFirst={0 === unitPosition}
                  isLast={units.length - 1 === unitPosition}
                  memberIds={members.map((index) => getCardId(attributes[index]))}
                  onToggleUse={() =>
                    setAttributes(
                      attributes.map((attribute, index) =>
                        members.includes(index) ? { ...attribute, inUse: !isInUse } : attribute
                      )
                    )
                  }
                  onMoveUp={() => onMoveUnit(unitPosition, unitPosition - 1)}
                  onMoveDown={() => onMoveUnit(unitPosition, unitPosition + 1)}
                  onDefineOrder={() => setOrderingGroup(unit.name)}
                  onEdit={() => setGroupPanel({ name: unit.name })}
                  onUngroup={() => setAttributes(ungroup(attributes, unit.name))}
                  onReorder={(from, to) =>
                    setAttributes(moveWithinGroup(attributes, members, from, to))
                  }
                >
                  {members.map((index) => renderCard(index, unitPosition, unit))}
                </GroupCard>
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>

      <UpsertProductTypeAttribute
        attribute={mapFamilyAttributeToDrawer(editedAttribute)}
        inheritedSource={
          editedAttribute && isInheritedAttribute(editedAttribute)
            ? { productTypeName, values: editedAttribute.sourceValues ?? [] }
            : undefined
        }
        existingNames={existingNames}
        isOpen={Boolean(attributePanel)}
        onClose={() => setAttributePanel(null)}
        onSaved={onAttributeSaved}
      />

      <OrderPanel
        items={valueOrderItems}
        title={t('titleDefineValuesOrder')}
        subtitle={orderingAttribute?.name ?? ''}
        isOpen={null !== orderingValuesIndex}
        onClose={() => setOrderingValuesIndex(null)}
        onSave={(orderedIds) =>
          updateAttribute(orderingValuesIndex, {
            ...orderingAttribute,
            values: orderedIds.map((id) => orderingAttribute.values[Number(id)]),
          })
        }
      />

      <OrderPanel
        items={unitOrderItems}
        title={t('titleDefineAttributesOrder')}
        subtitle={t('labelAttributes')}
        isOpen={isAttributesOrderOpen}
        onClose={() => setIsAttributesOrderOpen(false)}
        onSave={(orderedIds) =>
          setAttributes(
            orderedIds.flatMap((id) =>
              getUnitIndexes(units[Number(id)]).map((index) => attributes[index])
            )
          )
        }
      />

      <OrderPanel
        items={groupOrderItems}
        title={t('titleDefineAttributesOrder')}
        subtitle={orderingGroup ?? ''}
        isOpen={Boolean(orderedGroupUnit)}
        onClose={() => setOrderingGroup(null)}
        onSave={(orderedIds) => {
          const reordered = orderedIds.map((id) => attributes[orderedGroupIndexes[Number(id)]]);

          setAttributes(
            attributes.map((attribute, index) =>
              orderedGroupIndexes.includes(index)
                ? reordered[orderedGroupIndexes.indexOf(index)]
                : attribute
            )
          );
        }}
      />

      <GroupNamePanel
        title={t(groupPanel?.name ? 'titleEditGroup' : 'titleGroupAttributes')}
        name={groupPanel?.name}
        takenNames={groupNames.filter(
          (name) => !groupPanel?.name || getGroupKey(name) !== getGroupKey(groupPanel.name)
        )}
        isOpen={Boolean(groupPanel)}
        onClose={() => setGroupPanel(null)}
        onSave={onSaveGroupName}
      />

      <MoveToGroupPanel
        groupNames={groupNames}
        isOpen={null !== movingIndex}
        onClose={() => setMovingIndex(null)}
        onSave={(name) => setAttributes(moveToGroup(attributes, movingIndex, name))}
      />
    </Stack>
  );
};
